import React, { useState, useEffect, useRef } from 'react';
import { X, User, Phone, Mail, MapPin, Building, Check, AlertCircle, UserCheck } from 'lucide-react';
import clientesService from '../../services/clientesService';
import { formatCpfCnpj, formatPhone } from '../../utils/masks';
import { isValidCpfCnpj } from '../../utils/validators';
import CidadeAutocomplete from '../ui/CidadeAutocomplete';
import { useOrganizador } from '../../contexts/OrganizadorContext';

const NovoClienteModal = ({ 
  isOpen, 
  onClose, 
  onClienteCreated, 
  organizadorId, 
  defaultGrupo = 'vendedor',
  title = 'Novo Cliente'
}) => {
  const { organizadorAtivo } = useOrganizador();
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    email: '',
    cidade: '',
    estado: '',
    grupo: defaultGrupo,
    is_socio: false,
    fazenda_nome: '',
    fazenda_cidade: '',
    fazenda_estado: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [clientesSimilares, setClientesSimilares] = useState([]);
  const [buscandoDuplicados, setBuscandoDuplicados] = useState(false);
  const debounceRef = useRef(null);
  const [clientesSimilaresCpf, setClientesSimilaresCpf] = useState([]);
  const [buscandoDuplicadosCpf, setBuscandoDuplicadosCpf] = useState(false);
  const debounceCpfRef = useRef(null);

  // Busca clientes similares enquanto o usuário digita o nome
  useEffect(() => {
    const nome = formData.nome.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (nome.length < 3) {
      setClientesSimilares([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setBuscandoDuplicados(true);
        const results = await clientesService.searchByName(organizadorId, nome);
        setClientesSimilares(results?.slice(0, 4) || []);
      } catch {
        setClientesSimilares([]);
      } finally {
        setBuscandoDuplicados(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [formData.nome, organizadorId]);

  // Busca clientes com CPF/CNPJ parecido enquanto o usuário digita
  useEffect(() => {
    const digits = formData.cpf_cnpj.replace(/\D/g, '');
    if (debounceCpfRef.current) clearTimeout(debounceCpfRef.current);

    if (digits.length < 4) {
      setClientesSimilaresCpf([]);
      return;
    }

    debounceCpfRef.current = setTimeout(async () => {
      try {
        setBuscandoDuplicadosCpf(true);
        const results = await clientesService.searchByCpfCnpj(organizadorId, formData.cpf_cnpj);
        setClientesSimilaresCpf(results?.slice(0, 4) || []);
      } catch {
        setClientesSimilaresCpf([]);
      } finally {
        setBuscandoDuplicadosCpf(false);
      }
    }, 400);

    return () => clearTimeout(debounceCpfRef.current);
  }, [formData.cpf_cnpj, organizadorId]);

  const handleUsarExistente = async (cliente) => {
    try {
      const clienteCompleto = await clientesService.getById(organizadorId, cliente.id);
      onClienteCreated(clienteCompleto);
    } catch {
      onClienteCreated(cliente);
    }
    handleClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (formData.cpf_cnpj && !isValidCpfCnpj(formData.cpf_cnpj)) {
      newErrors.cpf_cnpj = 'CPF/CNPJ inválido';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let maskedValue = value;

    // Aplicar máscaras
    if (name === 'cpf_cnpj') {
      maskedValue = formatCpfCnpj(value);
    } else if (name === 'telefone') {
      maskedValue = formatPhone(value);
    } else if (name === 'estado' || name === 'fazenda_estado') {
      maskedValue = value.toUpperCase().slice(0, 2);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : maskedValue
    }));

    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Criar cliente
      const clienteData = {
        nome: formData.nome.trim(),
        cpf_cnpj: formData.cpf_cnpj || null,
        telefone: formData.telefone || null,
        email: formData.email || null,
        cidade: formData.cidade || null,
        estado: formData.estado || null,
        grupo: formData.grupo,
        is_socio: formData.is_socio
      };
      
      const newCliente = await clientesService.create(organizadorId, clienteData);
      
      // Se tem dados de fazenda, criar fazenda
      if (formData.fazenda_nome.trim()) {
        const fazendaData = {
          nome: formData.fazenda_nome.trim(),
          cidade: formData.fazenda_cidade || null,
          estado: formData.fazenda_estado || null
        };
        
        try {
          const fazenda = await clientesService.createFazenda(organizadorId, newCliente.id, fazendaData);
          newCliente.fazendas = [fazenda];
        } catch (fazendaError) {
          console.error('Erro ao criar fazenda:', fazendaError);
          // Não falha a criação do cliente por causa da fazenda
        }
      }
      
      onClienteCreated(newCliente);
      handleClose();
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      setErrors({ submit: 'Erro ao criar cliente. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nome: '',
      cpf_cnpj: '',
      telefone: '',
      email: '',
      cidade: '',
      estado: '',
      grupo: defaultGrupo,
      is_socio: false,
      fazenda_nome: '',
      fazenda_cidade: '',
      fazenda_estado: ''
    });
    setErrors({});
    setClientesSimilares([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600">Preencha os dados do cliente</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={saving}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Dados Principais */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <User size={18} className="text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900">Dados Principais</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg ${
                    errors.nome ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Digite o nome completo"
                  disabled={saving}
                  autoFocus
                />
                {errors.nome && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.nome}
                  </p>
                )}

                {/* Alerta de possíveis duplicados */}
                {(buscandoDuplicados || clientesSimilares.length > 0) && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    {buscandoDuplicados ? (
                      <p className="text-amber-700 text-sm flex items-center gap-2">
                        <span className="animate-spin inline-block w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full"></span>
                        Verificando duplicados...
                      </p>
                    ) : (
                      <>
                        <p className="text-amber-800 text-sm font-semibold mb-2 flex items-center gap-1">
                          <AlertCircle size={14} />
                          Cliente(s) similar(es) já cadastrado(s):
                        </p>
                        <div className="space-y-1">
                          {clientesSimilares.map(c => (
                            <div key={c.id} className="flex items-center justify-between bg-white rounded border border-amber-100 px-3 py-2">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{c.nome}</span>
                                {c.cidade && <span className="text-xs text-gray-500 ml-2">{c.cidade}/{c.estado}</span>}
                                {c.cpf_cnpj && <span className="text-xs text-gray-400 ml-2">{c.cpf_cnpj}</span>}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUsarExistente(c)}
                                className="ml-2 flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors whitespace-nowrap"
                              >
                                <UserCheck size={12} />
                                Usar este
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  name="cpf_cnpj"
                  value={formData.cpf_cnpj}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg ${
                    errors.cpf_cnpj ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="000.000.000-00"
                  disabled={saving}
                />
                {errors.cpf_cnpj && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.cpf_cnpj}
                  </p>
                )}

                {/* Alerta de CPF/CNPJ parecido já cadastrado */}
                {(buscandoDuplicadosCpf || clientesSimilaresCpf.length > 0) && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    {buscandoDuplicadosCpf ? (
                      <p className="text-amber-700 text-sm flex items-center gap-2">
                        <span className="animate-spin inline-block w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full"></span>
                        Verificando duplicados...
                      </p>
                    ) : (
                      <>
                        <p className="text-amber-800 text-sm font-semibold mb-2 flex items-center gap-1">
                          <AlertCircle size={14} />
                          CPF/CNPJ parecido já cadastrado:
                        </p>
                        <div className="space-y-1">
                          {clientesSimilaresCpf.map(c => (
                            <div key={c.id} className="flex items-center justify-between bg-white rounded border border-amber-100 px-3 py-2">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{c.nome}</span>
                                {c.cidade && <span className="text-xs text-gray-500 ml-2">{c.cidade}/{c.estado}</span>}
                                <span className="text-xs text-gray-400 ml-2">{c.cpf_cnpj}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUsarExistente(c)}
                                className="ml-2 flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors whitespace-nowrap"
                              >
                                <UserCheck size={12} />
                                Usar este
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone size={14} className="text-green-600" />
                  Telefone
                </label>
                <input
                  type="text"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="(00) 00000-0000"
                  disabled={saving}
                />
              </div>




            </div>

            {/* Debug: {JSON.stringify({produtos_premium: organizadorAtivo?.produtos_premium, usa_comissao_socios: organizadorAtivo?.usa_comissao_socios})} */}
            {(organizadorAtivo?.produtos_premium?.includes('comissao_socio') || organizadorAtivo?.usa_comissao_socios) && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_socio"
                  name="is_socio"
                  checked={formData.is_socio}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={saving}
                />
                <label htmlFor="is_socio" className="ml-2 block text-sm text-gray-900">
                  É sócio
                </label>
              </div>
            )}
          </div>

          {/* Localização */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">Localização</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cidade
                </label>
                <CidadeAutocomplete
                  value={formData.cidade}
                  onChange={(cidade) => setFormData(prev => ({ ...prev, cidade }))}
                  onEstadoChange={(estado) => setFormData(prev => ({ ...prev, estado }))}
                  placeholder="Digite a cidade..."
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <input
                  type="text"
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="UF"
                  maxLength="2"
                  disabled={saving}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Fazenda */}
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Building size={18} className="text-amber-600" />
              <h3 className="text-lg font-medium text-gray-900">Fazenda (Opcional)</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Fazenda
                </label>
                <input
                  type="text"
                  name="fazenda_nome"
                  value={formData.fazenda_nome}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="Ex: Fazenda Santa Rita"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cidade da Fazenda
                </label>
                <CidadeAutocomplete
                  value={formData.fazenda_cidade}
                  onChange={(cidade) => setFormData(prev => ({ ...prev, fazenda_cidade: cidade }))}
                  onEstadoChange={(estado) => setFormData(prev => ({ ...prev, fazenda_estado: estado }))}
                  placeholder="Cidade da fazenda..."
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado da Fazenda
                </label>
                <input
                  type="text"
                  name="fazenda_estado"
                  value={formData.fazenda_estado}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="UF"
                  maxLength="2"
                  disabled={saving}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {errors.submit}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 sm:pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 font-medium"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Criando...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Criar Cliente
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovoClienteModal;